import { CharacterLayerUrls, LayerName, REQUIRED_LAYERS, EQUIPMENT_LAYERS } from '../../types/layers';
import { LAYER_IMAGES, RACE_CONFIG, COLOR_CONFIG } from '../../lib/gameConstants';

type LayerUrlFunction = (char: CharacterLayerUrls) => string | undefined;

const LAYER_URL_MAPPING: Record<LayerName, LayerUrlFunction> = {
  'layer_background': (char) => char.layer_background_url,
  'layer_body': (char) => char.layer_body_url,
  'layer_skin': (char) => char.layer_skin_url,
  'layer_eyes': (char) => char.layer_eyes_url,
  'layer_tattoo': (char) => char.layer_tattoo_url,
  'layer_outfit': (char) => char.layer_outfit_url,
  'layer_frame': (char) => char.layer_frame_url,
  'layer_armor': (char) => char.layer_armor_url,
  'layer_weapon': (char) => char.layer_weapon_url,
  'layer_shield': (char) => char.layer_shield_url,
  'layer_accessory': (char) => char.layer_accessory_url,
};

export const getLayerUrl = (
  layerName: LayerName,
  character: CharacterLayerUrls
): string | undefined => {
  const urlFunction = LAYER_URL_MAPPING[layerName];
  if (!urlFunction) {
    console.warn('⚠️ [layerMapping] Unknown layer name:', layerName);
    return undefined;
  }

  const url = urlFunction(character);

  if (!url || url === 'null' || url === 'undefined' || url.trim() === '') {
    return undefined;
  }

  return url;
};

export const getLayerFallbackUrl = (
  layerName: LayerName,
  raceName?: string,
  colorName?: string
): string | undefined => {
  const safeRaceName = raceName || 'SIRIANS';
  const safeColorName = colorName || 'bubble';

  const raceKey = safeRaceName.toUpperCase() as keyof typeof RACE_CONFIG;
  const colorKey = safeColorName.toLowerCase() as keyof typeof COLOR_CONFIG;

  const raceConfig = RACE_CONFIG[raceKey];
  const colorConfig = COLOR_CONFIG[colorKey];

  switch (layerName) {
    case 'layer_skin':
      return colorConfig?.skinImage || LAYER_IMAGES.skins.eclipse;

    case 'layer_eyes':
      return LAYER_IMAGES.eyes.default;

    case 'layer_outfit':
      return LAYER_IMAGES.outfits.default;

    case 'layer_frame':
      return raceConfig?.frameImage || LAYER_IMAGES.frames.sirians;

    default:
      return undefined;
  }
};

export const shouldRenderLayer = (
  layerName: LayerName,
  character: CharacterLayerUrls & { layer_armor_id?: string }
): boolean => {
  if (layerName === 'layer_body') {
    const hasArmorId = Boolean(
      character.layer_armor_id &&
      character.layer_armor_id !== 'null' &&
      character.layer_armor_id !== 'undefined'
    );

    const hasArmorUrl = Boolean(
      character.layer_armor_url &&
      character.layer_armor_url !== 'null' &&
      character.layer_armor_url !== 'undefined' &&
      character.layer_armor_url.trim() !== ''
    );

    const hasArmor = hasArmorUrl || hasArmorId;
    return !hasArmor;
  }

  if (REQUIRED_LAYERS.has(layerName)) {
    return true;
  }

  const url = getLayerUrl(layerName, character);
  return Boolean(url);
};

export const isValidUrl = (url?: string): boolean => {
  if (!url) return false;
  if (url === 'null' || url === 'undefined') return false;
  if (url.trim() === '') return false;
  return true;
};
